

import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/auth/authService';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { setActiveShopId } from '../services/tenantQuery';
import type { UserProfile } from '../services/auth/authService';

// For backward compatibility with existing app
import type { User } from '../types';
export type { User };

interface AuthContextType {
    user: User | null;
    firebaseUser: UserProfile | null;
    loading: boolean;
    authError: string | null;
    profileError: boolean; // authenticated, but the profile read failed — show an actionable retry
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    register: (userData: any) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// RESILIENT PROFILE LOAD (Jose 2026-09-24): the post-login "Loading…" trap — a single Firestore
// read (users/{uid}) can hang SILENTLY on a flaky mobile network and never resolve, so setLoading
// (false) never runs and the app spins forever. Race each attempt against a timeout and retry a few
// times so a transient wedge self-heals. Worst case falls through to an actionable retry (below),
// never an infinite spinner. BEHAVIOR-PIN: self-healing-resilience-always-a-go.
async function loadProfileResilient(uid: string, tries = 3, perTryMs = 7000): Promise<UserProfile> {
    let lastErr: unknown;
    for (let i = 0; i < tries; i++) {
        try {
            return await Promise.race([
                authService.getUserProfile(uid),
                new Promise<never>((_, rej) => setTimeout(() => rej(new Error('profile-read-timeout')), perTryMs)),
            ]);
        } catch (e) {
            lastErr = e;
            console.warn(`[Auth] profile read attempt ${i + 1}/${tries} failed:`, (e as Error)?.message);
        }
    }
    throw lastErr instanceof Error ? lastErr : new Error('profile-read-failed');
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [firebaseUser, setFirebaseUser] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState<string | null>(null);
    const [profileError, setProfileError] = useState(false);

    useEffect(() => {
        // Subscribe to Firebase auth state changes
        const unsubscribe = onAuthStateChanged(auth, async (firebaseAuthUser) => {
            setAuthError(null);

            if (firebaseAuthUser) {
                // User is signed in - get their profile from Firestore
                try {
                    const userProfile = await loadProfileResilient(firebaseAuthUser.uid);
                    setProfileError(false);

                    setFirebaseUser(userProfile);

                    // Multi-tenant: feed THIS user's shop into the tenant layer so scopedQuery() filters
                    // by their membership (not shops.docs[0]). BEHAVIOR-PIN: production-safety-net 2026-09-11.
                    if (userProfile.shopId) setActiveShopId(userProfile.shopId);

                    // Read location from localStorage (set by LocationContext)
                    const savedLocation = localStorage.getItem('slikpos_current_location') || 'loc-barber-lounge';

                    // For backward compatibility, create a User object
                    const compatUser: User = {
                        id: userProfile.uid,
                        email: userProfile.email,
                        // Honor the `isOwner` flag (real owners often have users-doc role 'customer'/'staff'
                        // + isOwner:true) → resolve them to OWNER in the staff app so usePermission grants
                        // full access when they log in as THEMSELVES (no PIN operator). The users doc is
                        // NOT changed, so the customer portal (separate app) is unaffected. (Jose 2026-09-27)
                        role: ((userProfile as any).isOwner === true ? 'owner' : userProfile.role) as any,
                        firstName: userProfile.name?.split(' ')[0] || '',
                        lastName: userProfile.name?.split(' ').slice(1).join(' ') || '',
                        phone: userProfile.phone || '',
                        createdAt: userProfile.createdAt,
                        updatedAt: userProfile.lastLogin,
                        locationId: savedLocation,
                        permissions: userProfile.role === 'staff' ? [
                            { resource: 'bookings', actions: ['create', 'read', 'update', 'delete'] },
                            { resource: 'services', actions: ['create', 'read', 'update', 'delete'] },
                            { resource: 'barbers', actions: ['create', 'read', 'update', 'delete'] }
                        ] : [
                            { resource: 'bookings', actions: ['create', 'read'] }
                        ],
                        digitalCoins: 0,
                        points: 0,
                        tier: 'BRONZE' as any,
                        totalVisits: 0
                    };
                    setUser(compatUser);
                } catch (error) {
                    // Do NOT auto-signOut here: on a transient read hang that just bounces a VALID
                    // session back to login in a loop. Keep the Firebase session, flag profileError,
                    // and let ProtectedRoute show a Retry button (a reload re-runs this with a fresh
                    // read). BEHAVIOR-PIN: self-healing-resilience-always-a-go.
                    console.error('AuthContext: Error getting user profile', error);
                    setAuthError('Slow connection — could not load your profile.');
                    setProfileError(true);
                    setFirebaseUser(null);
                    setUser(null);
                }
            } else {
                setProfileError(false);
                setFirebaseUser(null);
                setUser(null);
            }

            setLoading(false);
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, []);

    const login = async (email: string, password: string) => {
        setAuthError(null);
        try {
            await authService.signIn(email, password);
            // User state will be updated by onAuthStateChanged listener
        } catch (error) {
            console.error('AuthContext: Login failed', error);
            throw error;
        }
    };

    const logout = async () => {
        try {
            // Clean up all app-specific cached data (slikpos_* keys)
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('slikpos_')) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));

            // Clean up other known auth/session keys
            localStorage.removeItem('authToken');
            localStorage.removeItem('currentUser');
            sessionStorage.clear();

            await authService.signOut();
            // BULLETPROOF LOGOUT (Jose 2026-09-23): "logout does nothing" — the Firebase signOut
            // succeeds but the React redirect wasn't firing (stale state / route not re-evaluating).
            // Hard-navigate to /login so a logout ALWAYS lands you on the login screen, fresh, no
            // matter the state. This also busts any stale in-memory session on that tab.
            window.location.assign('/login');
        } catch (error) {
            console.error('AuthContext: Logout failed', error);
            // Even if signOut throws, force the escape — never trap the user in a session.
            try { window.location.assign('/login'); } catch { /* */ }
        }
    };

    const register = async (userData: { email: string; password: string; name?: string; role: 'staff' | 'customer' }) => {
        setAuthError(null);
        try {
            await authService.signUp(
                userData.email,
                userData.password,
                userData.role,
                userData.name
            );
            // User state will be updated by onAuthStateChanged listener
        } catch (error) {
            console.error('AuthContext: Registration failed', error);
            throw error;
        }
    };

    return (
        <AuthContext.Provider value={{ user, firebaseUser, loading, authError, profileError, login, logout, register }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};